import React, { ReactElement, useEffect } from 'react'
import gql from 'graphql-tag'
import TextSearchDropdown from '../TextSearchDropdown/TextSearchDropdown'
import { useGetSingleFileNumberLazyQuery, useFileNumbersSearchQuery, InnerFileNumberFragment } from '../../../generated/graphql'
import SearchDisplay, { SearchDisplayProps, InnerSearchLogicProps } from '../SearchDisplay/SearchDisplay'
import { DefaultInputProps } from '../../General/Inputs/HookedTextArea'


export const FileNumbersSearchQuery = gql`
    query FileNumbersSearch($search: String, $terminal: Int, $supplier: Int, $archived: Boolean) {
        fileNumberGetAll(where: {and: [{isArchived: {eq: $archived}}, { number: {contains: $search}}, {terminalKey: { eq: $terminal}}, {supplierKey: {eq: $supplier}}]}, order: [{ number: DESC }]) {
            ...innerFileNumber
        }
    }
`

interface Props extends DefaultInputProps<InnerFileNumberFragment> {
    onSelect?(fileNumber: InnerFileNumberFragment)
    prefilledFileNumberKey?: number
    supplierKey?: number
    terminalKey?: number
    disabled?: boolean
}

export default function FileNumberSearch(props: Props): ReactElement {

    const [executeGetSingleFileNumber, { data, loading }] = useGetSingleFileNumberLazyQuery()

    const { prefilledFileNumberKey } = props

    useEffect(() => {
        if (prefilledFileNumberKey) {
            executeGetSingleFileNumber({ variables: { key: prefilledFileNumberKey } })
        }
    }, [prefilledFileNumberKey, executeGetSingleFileNumber])

    // Only show prefilled data if we have a valid prefilledFileNumberKey and the data matches
    const prefilledData = prefilledFileNumberKey && data && data.fileNumberGetSingle ? data.fileNumberGetSingle : undefined
    const prefilledDataDisplay = prefilledFileNumberKey && data && data.fileNumberGetSingle ? `${data.fileNumberGetSingle.number}` : undefined

    // Disable the search if supplier or terminal are not selected
    const isDisabled = props.disabled || !props.supplierKey || !props.terminalKey

    return <TextSearchDropdown<InnerFileNumberFragment>
        onSelect={props.onSelect}
        mapEntityToKey={e => parseInt(e.fileNumberKey.toString())}
        searchWrapper={<Inner searchTerm="" onSelect={() => null} supplierKey={props.supplierKey} terminalKey={props.terminalKey} />}
        isLoadingPrefilled={loading}
        prefilledEntity={prefilledData}
        prefilledEntityDisplay={prefilledDataDisplay}
        {...props}
        disabled={isDisabled}
    />
}

const Inner = (props: InnerSearchLogicProps<InnerFileNumberFragment>) => {

    // Skip query if supplier or terminal are not provided
    const shouldSkip = !props.supplierKey || !props.terminalKey
    const { data, loading, error } = useFileNumbersSearchQuery({
        variables: { search: props.searchTerm, terminal: props.terminalKey, supplier: props.supplierKey, archived: false },
        skip: shouldSkip
    })

    const showData = !loading && !error && !shouldSkip

    const results = showData && data?.fileNumberGetAll
        ? data.fileNumberGetAll.map(fn => {
            const display = fn.number
            return { key: fn.fileNumberKey, object: fn, display }
        })
        : []

    const displayProps: SearchDisplayProps<InnerFileNumberFragment> = {
        onSelect: props.onSelect,
        groups: [{ title: "File Numbers", results }],
        isLoading: loading,
        searchTerm: props.searchTerm,
        error
    }

    return <SearchDisplay {...displayProps} />
}